import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, X, Edit2, Shield, ShieldCheck, User as UserIcon, EyeOff, Bot, Upload, Pencil, Headphones, KeyRound, Cpu, Globe, Eye, Plug, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { getIaInfo, atualizarIa, uploadBrandingFile, getConfiguracoesCompletas } from "../services/branding";
import { iaHealthcheck } from "../services/ia";
import { anexoUrlAbsoluto } from "../services/tarefas";
import { listarTiposProjeto, listarCategoriasUsuario, definirCategoriasUsuario } from "../services/tiposProjeto";
import { listarProjetos } from "../services/projetos";
import { listarSetores } from "../services/setores";
import { listarFuncoesTarefa } from "../services/funcoesTarefa";
import {
  listarUsuarios,
  criarUsuario,
  atualizarUsuario,
  desativarUsuario,
  Perfil,
  Usuario,
} from "../services/usuariosAdmin";
import { useConfirm } from "../components/ConfirmProvider";
import { SearchInput, matchBusca } from "../components/SearchInput";
import { VINCULO_LABEL, VINCULO_BADGE, VINCULO_OPCOES } from "../utils/vinculo";

const PERFIL_LABEL: Record<Perfil, string> = {
  funcionario: "Funcionário",
  suporte: "Suporte",
  gestor: "Gestor",
  admin: "Admin",
  cliente: "Cliente",
};

const PERFIL_BADGE: Record<Perfil, string> = {
  funcionario: "bg-slate-100 text-slate-700",
  suporte: "bg-emerald-100 text-emerald-700",
  gestor: "bg-blue-100 text-blue-700",
  admin: "bg-purple-100 text-purple-700",
  cliente: "bg-amber-100 text-amber-700",
};

const PERFIL_ICON: Record<Perfil, any> = {
  funcionario: UserIcon,
  suporte: Headphones,
  gestor: Shield,
  admin: ShieldCheck,
  cliente: UserIcon,
};

export function Usuarios() {
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [busca, setBusca] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Usuario | null>(null);
  const [aba, setAba] = useState<"internos" | "clientes">("internos");

  const { data: usuarios = [], isLoading } = useQuery({
    queryKey: ["usuarios"],
    queryFn: listarUsuarios,
    enabled: can(["gestor", "admin"]),
  });

  const ehAdmin = can(["admin"]);
  const ehGestor = can(["gestor"]);

  const criarM = useMutation({
    mutationFn: criarUsuario,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      setShowForm(false);
      setEditing(null);
    },
  });

  const updateM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarUsuario(id, dados),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      setShowForm(false);
      setEditing(null);
    },
  });

  const desativarM = useMutation({
    mutationFn: desativarUsuario,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["usuarios"] }),
  });

  // A exclusão permanente de usuário foi retirada da interface — ver o comentário
  // na coluna de ações. A rota do backend continua existindo para casos extremos.
  if (!can(["gestor", "admin"])) {
    return <div className="text-slate-500">Acesso restrito.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Usuários</h1>
          <p className="text-slate-500 mt-1">Gerencie quem tem acesso ao sistema e qual o nível de permissão.</p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Plus size={16} /> Novo usuário
        </button>
      </div>

      {ehAdmin && <CardIA />}

      {/* Tabs Internos / Clientes */}
      <div className="inline-flex rounded-lg bg-slate-100 p-1">
        <button
          type="button"
          onClick={() => setAba("internos")}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            aba === "internos" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          Equipe interna
        </button>
        <button
          type="button"
          onClick={() => setAba("clientes")}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            aba === "clientes" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          Clientes
        </button>
      </div>

      <SearchInput value={busca} onChange={setBusca} placeholder="Buscar por nome, e-mail ou perfil…" />

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Nome</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500">E-mail</th>
              <th className="px-4 py-3 text-center font-medium text-slate-500">{aba === "clientes" ? "Projeto vinculado" : "Perfil"}</th>
              <th className="px-4 py-3 text-center font-medium text-slate-500">Status</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(() => {
              const filtradosPorAba = usuarios.filter((u) => aba === "clientes" ? u.perfil === "cliente" : u.perfil !== "cliente");
              const filtrados = matchBusca(filtradosPorAba, busca, [(u) => u.nome, (u) => u.email, (u) => u.perfil]);
              if (isLoading) return (<tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>);
              if (filtrados.length === 0) return (<tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">{busca ? "Nenhum usuário encontrado" : aba === "clientes" ? "Nenhum cliente cadastrado" : "Nenhum usuário"}</td></tr>);
              return filtrados.map((u) => {
              const Icon = PERFIL_ICON[u.perfil] || UserIcon;
              const isSelf = u.id === user?.id;
              const ehAdminAlvo = u.perfil === "admin";
              const podeEditar = ehAdmin || !ehAdminAlvo;       // gestor não edita admin
              const podeDesativar = !isSelf && u.ativo && (ehAdmin || !ehAdminAlvo);
                          return (
                <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2">
                    <Icon size={14} className="text-slate-400" />
                    {u.nome}
                    {isSelf && <span className="text-[10px] uppercase font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">você</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3 text-center">
                    {aba === "clientes" ? (
                      u.projetoVisivelId ? (
                        <span className="text-xs text-slate-600 italic" title={u.projetoVisivelId}>
                          {u.projetoVisivelId.slice(0, 8)}…
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">sem projeto</span>
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${PERFIL_BADGE[u.perfil]}`}>
                          {PERFIL_LABEL[u.perfil]}
                        </span>
                        {u.vinculo && VINCULO_LABEL[u.vinculo] && (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${VINCULO_BADGE[u.vinculo]}`}>
                            {VINCULO_LABEL[u.vinculo]}
                          </span>
                        )}
                        {/* Selo Atendente — só exibição (jul/2026). */}
                        {(u as any).ehAtendente && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                            Atendente
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${u.ativo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {u.ativo ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {podeEditar && (
                      <button
                        onClick={() => { setEditing(u); setShowForm(true); }}
                        className="text-blue-600 hover:text-blue-700"
                        title="Editar"
                      >
                        <Edit2 size={14} />
                      </button>
                    )}
                    {podeDesativar && (
                      <button
                        onClick={async () => {
                          // Responde a dúvida da Thainá ("as tarefas ficam sem
                          // dono ou são excluídas?") na hora em que ela aparece.
                          const ok = await confirm({
                            title: "Desativar usuário",
                            message:
                              `Desativar ${u.nome}?\n\n` +
                              `Nada é apagado. Ele perde o acesso ao sistema, mas todo o histórico continua: entregas, tempo trabalhado e ajustes seguem aparecendo nos relatórios (com o selo "ex").\n\n` +
                              `As tarefas dele NÃO são excluídas nem ficam sem dono — continuam no nome dele. Se houver tarefa em aberto, passe para outra pessoa antes, senão ela fica parada no quadro sem ninguém para tocar.`,
                            danger: true,
                            confirmText: "Desativar",
                          });
                          if (ok) desativarM.mutate(u.id);
                        }}
                        className="text-slate-400 hover:text-amber-500"
                        title="Desativar"
                      >
                        <EyeOff size={14} />
                      </button>
                    )}
                    {/* "Excluir permanentemente" SAIU DA TELA (pedido da Thainá,
                        ago/2026: "remover essa opção pra não acontecer de alguém
                        excluir sem querer").

                        O botão apagava em cascata as TAREFAS atribuídas à pessoa
                        e os PROJETOS onde ela é responsável. Na conta dela mesma
                        isso levaria 57 projetos; no Diego, 206 tarefas. Um clique
                        errado numa linha vizinha ao "Desativar" custaria caro
                        demais, e "Desativar" já resolve todos os casos reais
                        (tira o acesso e preserva o histórico).

                        A rota `DELETE /usuarios/:id/permanente` continua no
                        backend (admin + ?forcar=true) pra um caso extremo, como
                        LGPD — só não é mais alcançável por engano pela interface. */}
                  </td>
                </tr>
              );
            });
            })()}
          </tbody>
        </table>
      </div>

      {showForm && (
        <UsuarioFormModal
          editing={editing}
          ehAdmin={ehAdmin}
          ehGestor={ehGestor}
          perfilDefault={aba === "clientes" ? "cliente" : "funcionario"}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSubmit={(dados) => {
            if (editing) updateM.mutate({ id: editing.id, dados });
            else criarM.mutate(dados as any);
          }}
          loading={criarM.isPending || updateM.isPending}
          error={(criarM.error as any)?.response?.data?.error?.message || (updateM.error as any)?.response?.data?.error?.message}
        />
      )}
    </div>
  );
}

interface FormProps {
  editing: Usuario | null;
  onClose: () => void;
  onSubmit: (dados: any) => void;
  loading: boolean;
  error?: string;
  ehAdmin: boolean;
  ehGestor: boolean;
  perfilDefault?: Perfil;
}

function UsuarioFormModal({ editing, onClose, onSubmit, loading, error, ehAdmin, ehGestor, perfilDefault }: FormProps) {
  const qc = useQueryClient();
  const [nome, setNome] = useState(editing?.nome || "");
  const [email, setEmail] = useState(editing?.email || "");
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [perfil, setPerfil] = useState<Perfil>(editing?.perfil || perfilDefault || "funcionario");
  const [ativo, setAtivo] = useState(editing?.ativo ?? true);
  const [projetoVisivelId, setProjetoVisivelId] = useState<string>(editing?.projetoVisivelId || "");
  // Dados profissionais (PDF — Cadastro de colaborador)
  const [cargo, setCargo] = useState(editing?.cargo || "");
  const [nivel, setNivel] = useState<string>((editing as any)?.nivel || "");
  const [vinculo, setVinculo] = useState<string>((editing as any)?.vinculo || "");
  // Selo "Atendente" — só exibição, não muda permissão.
  const [ehAtendente, setEhAtendente] = useState<boolean>(!!(editing as any)?.ehAtendente);
  // Setor/equipe — agrupa a Produtividade (texto livre com sugestões).
  const [setor, setSetor] = useState<string>((editing as any)?.setor || "");
  // Função padrão nas tarefas — pré-preenche a função ao alocar a pessoa num card.
  const [funcaoPadrao, setFuncaoPadrao] = useState<string>((editing as any)?.funcaoPadrao || "");
  const [telefone, setTelefone] = useState(editing?.telefone || "");
  const [whatsapp, setWhatsapp] = useState(editing?.whatsapp || "");
  const [dataNascimento, setDataNascimento] = useState(editing?.dataNascimento ? editing.dataNascimento.slice(0, 10) : "");
  const [entrouEmpresaEm, setEntrouEmpresaEm] = useState(editing?.entrouEmpresaEm ? editing.entrouEmpresaEm.slice(0, 10) : "");
  const [fusoHorario, setFusoHorario] = useState(editing?.fusoHorario || "");
  const [custoHora, setCustoHora] = useState<string>(editing?.custoHora != null ? String(editing.custoHora) : "");

  // Lista de projetos pra o select de cliente (carrega só quando necessário)
  const ehCliente = perfil === "cliente";
  const { data: projetosData } = useQuery({
    queryKey: ["projetos-list-clientes"],
    queryFn: () => listarProjetos({ perPage: 200, interno: "all" }),
    enabled: ehCliente,
  });
  const projetosList: Array<{ id: string; nome: string }> = (projetosData as any)?.projetos || [];

  // Categorias: gestor e admin podem atribuir. Aplica só pra perfis internos restritos.
  const podeAtribuirCategoria = !!editing && (ehAdmin || ehGestor) && (perfil === "funcionario" || perfil === "suporte");
  const { data: setores = [] } = useQuery({ queryKey: ["setores"], queryFn: () => listarSetores() });
  const { data: funcoes = [] } = useQuery({ queryKey: ["funcoes-tarefa"], queryFn: () => listarFuncoesTarefa() });
  const { data: tiposAtivos = [] } = useQuery({
    queryKey: ["tipos-projeto", "ativos-form"],
    queryFn: () => listarTiposProjeto(),
    enabled: podeAtribuirCategoria,
  });
  // ATENÇÃO: NÃO usar default `= []` na desestruturação aqui — `data` precisa
  // ficar `undefined` enquanto a query carrega, pra o effect saber distinguir
  // "ainda não carregou" de "carregou e está vazio". Antes, o default fazia o
  // effect hidratar com [] antes do fetch terminar, e os IDs reais nunca eram
  // aplicados.
  const { data: categoriasAtuais } = useQuery({
    queryKey: ["usuario-categorias", editing?.id],
    queryFn: () => listarCategoriasUsuario(editing!.id),
    enabled: podeAtribuirCategoria,
  });
  const [categoriasIds, setCategoriasIds] = useState<string[]>([]);
  const [categoriasHidratadas, setCategoriasHidratadas] = useState(false);
  useEffect(() => {
    if (podeAtribuirCategoria && !categoriasHidratadas && categoriasAtuais !== undefined) {
      setCategoriasIds(categoriasAtuais.map((c) => c.id));
      setCategoriasHidratadas(true);
    }
  }, [categoriasAtuais, podeAtribuirCategoria, categoriasHidratadas]);

  const salvarCategoriasM = useMutation({
    mutationFn: (ids: string[]) => definirCategoriasUsuario(editing!.id, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["usuario-categorias", editing?.id] }),
  });

  function toggleCategoria(id: string) {
    setCategoriasIds((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  }

  // Mostra campos profissionais só pra perfis internos (cliente não tem cargo/custo).
  const ehInterno = perfil !== "cliente";

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const payload: any = { nome, email, perfil };
    if (senha) payload.senha = senha;
    if (editing) payload.ativo = ativo;
    if (perfil === "cliente") {
      payload.projetoVisivelId = projetoVisivelId || null;
    } else {
      // Campos profissionais. Strings vazias viram null (campo limpo).
      payload.cargo = cargo.trim() || null;
      payload.nivel = nivel || null;
      payload.vinculo = vinculo || null;
      payload.ehAtendente = ehAtendente;
      payload.setor = setor.trim() || null;
      payload.funcaoPadrao = funcaoPadrao.trim() || null;
      payload.telefone = telefone.trim() || null;
      payload.whatsapp = whatsapp.trim() || null;
      payload.dataNascimento = dataNascimento || null;
      payload.entrouEmpresaEm = entrouEmpresaEm || null;
      payload.fusoHorario = fusoHorario.trim() || null;
      // custoHora aceita "13,63" ou "13.63"
      const c = custoHora.trim().replace(",", ".");
      payload.custoHora = c === "" ? null : Number.isFinite(parseFloat(c)) ? parseFloat(c) : null;
    }
    onSubmit(payload);
    // Salva categorias em paralelo (só na edição). Não bloqueia o fechamento do modal.
    if (podeAtribuirCategoria) {
      salvarCategoriasM.mutate(categoriasIds);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">{editing ? "Editar usuário" : "Novo usuário"}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        {/* autoComplete="off" no form + campos com nomes não-padrão evitam o
            navegador/gerenciador de senha pré-preencher e-mail e senha de OUTRO
            usuário (ex.: o e-mail do admin logado) ao criar um novo cadastro. */}
        <form onSubmit={submit} className="p-5 space-y-3" autoComplete="off">
          <div>
            <label className="text-xs text-slate-600">Nome</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">E-mail</label>
            <input
              type="email"
              name="usuario-email-novo"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">{editing ? "Nova senha (deixe vazio pra manter)" : "Senha"}</label>
            <div className="relative">
              <input
                type={mostrarSenha ? "text" : "password"}
                name="usuario-senha-nova"
                autoComplete="new-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required={!editing}
                minLength={6}
                className="w-full px-3 py-2 pr-10 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => setMostrarSenha((v) => !v)}
                tabIndex={-1}
                aria-label={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-600"
              >
                {mostrarSenha ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600">Perfil</label>
            <select
              value={perfil}
              onChange={(e) => setPerfil(e.target.value as Perfil)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
              disabled={!ehAdmin && editing?.perfil === "admin"}
            >
              <option value="funcionario">Funcionário — vê seus dados, sem financeiro</option>
              <option value="suporte">Suporte — igual funcionário, mas só projetos internos</option>
              <option value="gestor">Gestor — vê tudo, inclusive financeiro</option>
              {ehAdmin && <option value="admin">Admin — gerencia usuários</option>}
              <option value="cliente">Cliente — só vê o projeto vinculado, sem dados internos</option>
            </select>
            {!ehAdmin && (
              <p className="text-[10px] text-slate-400 mt-1">Apenas admin pode definir o perfil "admin".</p>
            )}
          </div>

          {ehInterno && (
            <div className="border-t border-slate-100 pt-3 space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Dados profissionais</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-600">Cargo</label>
                  <input type="text" value={cargo} onChange={(e) => setCargo(e.target.value)} placeholder="Ex.: Designer, Editor de vídeo" className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Nível</label>
                  <select value={nivel} onChange={(e) => setNivel(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500">
                    <option value="">—</option>
                    <option value="junior">Júnior</option>
                    <option value="pleno">Pleno</option>
                    <option value="senior">Sênior</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-600">Vínculo <span className="text-slate-400 font-normal">(selo)</span></label>
                  <select value={vinculo} onChange={(e) => setVinculo(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500">
                    {VINCULO_OPCOES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                {/* Selo Atendente — só exibição (jul/2026). Marca quem faz atendimento
                    ao cliente; não muda permissão (a cobrança segue pelo "Atendimento"
                    do projeto). */}
                <div>
                  <label className="text-xs text-slate-600">Atendente <span className="text-slate-400 font-normal">(selo)</span></label>
                  <label className="flex items-center gap-2 mt-1.5 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none">
                    <input type="checkbox" checked={ehAtendente} onChange={(e) => setEhAtendente(e.target.checked)} className="w-4 h-4 accent-sky-600" />
                    Faz atendimento ao cliente
                  </label>
                </div>
                {/* Setor/equipe — dropdown do catálogo (Admin → Setores da equipe).
                    Agrupa a Produtividade e o "Destaque do mês". */}
                <div>
                  <label className="text-xs text-slate-600">Setor / equipe</label>
                  <select
                    value={setor}
                    onChange={(e) => setSetor(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 dark:bg-slate-800"
                  >
                    <option value="">— sem setor</option>
                    {setores.map((s) => (
                      <option key={s.id} value={s.nome}>{s.nome}</option>
                    ))}
                    {/* Mantém visível um setor legado que já esteja na pessoa mas
                        não esteja mais no catálogo (não some do dropdown). */}
                    {setor && !setores.some((s) => s.nome === setor) && (
                      <option value={setor}>{setor} (fora do catálogo)</option>
                    )}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Crie/edite os setores em <strong>Admin → Setores da equipe</strong>.
                  </p>
                </div>

                {/* FUNÇÃO PADRÃO nas tarefas — pedido da Thainá (ago/2026): quem
                    sempre faz a mesma coisa já entra no card com a função certa,
                    e quem varia (editor/filmmaker/fotógrafo) é só trocar lá. */}
                <div>
                  <label className="text-xs text-slate-600">Função padrão nas tarefas</label>
                  <select
                    value={funcaoPadrao}
                    onChange={(e) => setFuncaoPadrao(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 dark:bg-slate-800"
                  >
                    <option value="">— sem função padrão</option>
                    {funcoes.map((f) => (
                      <option key={f.id} value={f.nome}>{f.nome}</option>
                    ))}
                    {/* Função legada (saiu do catálogo) continua visível na pessoa. */}
                    {funcaoPadrao && !funcoes.some((f) => f.nome === funcaoPadrao) && (
                      <option value={funcaoPadrao}>{funcaoPadrao} (fora do catálogo)</option>
                    )}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Vem preenchida ao alocar a pessoa numa tarefa — e dá pra trocar no card.
                    Mudar aqui <strong>não</strong> altera tarefas já criadas.
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-600">Na empresa desde</label>
                  <input type="date" value={entrouEmpresaEm} onChange={(e) => setEntrouEmpresaEm(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Telefone</label>
                  <input type="tel" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(00) 0000-0000" className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">WhatsApp</label>
                  <input type="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="(00) 00000-0000" className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Data de nascimento</label>
                  <input type="date" value={dataNascimento} onChange={(e) => setDataNascimento(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
                  {dataNascimento && (
                    <p className="text-[10px] text-slate-400 mt-0.5">Aparece como aniversário no calendário.</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-600">Fuso horário</label>
                  <input type="text" value={fusoHorario} onChange={(e) => setFusoHorario(e.target.value)} placeholder="America/Sao_Paulo" className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 font-mono" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-600 flex items-center gap-1">
                  <Shield size={11} className="text-amber-600" />
                  Custo por hora (R$)
                  <span className="text-[10px] text-slate-400 font-normal">— visível só pra gestor/admin</span>
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={custoHora}
                  onChange={(e) => setCustoHora(e.target.value)}
                  placeholder="Ex.: 13,63"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 font-mono"
                />
                <p className="text-[10px] text-slate-500 mt-0.5">Usado pra cruzar horas trabalhadas vs custo no projeto.</p>
              </div>
            </div>
          )}

          {ehCliente && (
            <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
              <label className="text-xs font-medium text-blue-700 block mb-1">
                Projeto vinculado <span className="text-red-500">*</span>
              </label>
              <select
                value={projetoVisivelId}
                onChange={(e) => setProjetoVisivelId(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 bg-white"
              >
                <option value="">Selecione o projeto…</option>
                {projetosList.map((p) => (
                  <option key={p.id} value={p.id}>{p.nome}</option>
                ))}
              </select>
              <p className="text-[10px] text-blue-700 mt-1">
                Esse usuário-cliente só verá esse projeto no portal. Para vincular a múltiplos projetos, crie um usuário por projeto.
              </p>
            </div>
          )}
          {podeAtribuirCategoria && (
            <div className="border-t border-slate-100 pt-3">
              <label className="text-xs text-slate-600 block mb-1">
                Categorias <span className="text-slate-400 font-normal">(filtra projetos/tarefas que este usuário enxerga — vazio = vê tudo do perfil)</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {tiposAtivos.length === 0 && (
                  <span className="text-xs text-slate-400 italic">Nenhum tipo de projeto cadastrado.</span>
                )}
                {tiposAtivos.map((t) => {
                  const selecionado = categoriasIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleCategoria(t.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        selecionado
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
                      }`}
                      style={selecionado && t.cor ? { backgroundColor: t.cor, borderColor: t.cor } : undefined}
                    >
                      {selecionado && "✓ "}{t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {editing && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
              Conta ativa
            </label>
          )}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              {loading ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CardIA() {
  const qc = useQueryClient();
  const [editando, setEditando] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: ia } = useQuery({ queryKey: ["ia-info"], queryFn: getIaInfo });
  // Configs avançadas (chave/modelo/URL) — só admin recebe a chave em claro do backend.
  const { data: configs } = useQuery({ queryKey: ["configuracoes"], queryFn: getConfiguracoesCompletas });
  const [nome, setNome] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [semTools, setSemTools] = useState(false);
  const [temperatura, setTemperatura] = useState("");
  const [mostrarChave, setMostrarChave] = useState(false);
  const [erro, setErro] = useState("");
  const [testeResult, setTesteResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const testarM = useMutation({
    mutationFn: () =>
      iaHealthcheck({
        baseUrl: baseUrl.trim() || undefined,
        apiKey: apiKey.trim() || undefined,
      }),
    onMutate: () => setTesteResult(null),
    onSuccess: (r) => {
      setTesteResult(
        r.ok
          ? { ok: true, msg: `Conectado (HTTP ${r.status ?? 200})` }
          : { ok: false, msg: r.erro || `Falhou (HTTP ${r.status ?? "?"})` },
      );
    },
    onError: (e: any) =>
      setTesteResult({ ok: false, msg: e?.response?.data?.error?.message || e?.message || "Erro ao testar" }),
  });

  const salvarM = useMutation({
    mutationFn: () => {
      // Empacota só o que mudou em relação ao salvo (null limpa, undefined ignora).
      const payload: Record<string, string | null> = { iaNome: nome.trim() || null };
      const orig = configs || {};
      // Modelo / Base URL — salva trim ou limpa
      if ((model.trim() || "") !== (orig.iaModel || "")) {
        payload.iaModel = model.trim() || null;
      }
      if ((baseUrl.trim() || "") !== (orig.iaBaseUrl || "")) {
        payload.iaBaseUrl = baseUrl.trim() || null;
      }
      const semToolsAtual = orig.iaSemTools === "true";
      if (semTools !== semToolsAtual) {
        payload.iaSemTools = semTools ? "true" : null;
      }
      const tempAtual = (orig.iaTemperatura || "").trim();
      if (temperatura.trim() !== tempAtual) {
        payload.iaTemperatura = temperatura.trim() || null;
      }
      // Chave: só envia se foi tocada (não vazia OU usuário clicou "limpar").
      // Vazio = manter atual. Pra apagar, usar botão dedicado.
      if (apiKey.trim()) {
        payload.iaApiKey = apiKey.trim();
      }
      return atualizarIa(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ia-info"] });
      qc.invalidateQueries({ queryKey: ["configuracoes"] });
      setEditando(false);
      setApiKey("");
    },
    onError: (e: any) => setErro(e?.response?.data?.error?.message || "Erro ao salvar"),
  });

  const limparChaveM = useMutation({
    mutationFn: () => atualizarIa({ iaApiKey: null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["configuracoes"] });
      setApiKey("");
    },
  });

  const uploadM = useMutation({
    mutationFn: (file: File) => uploadBrandingFile("iaFotoUrl", file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-info"] }),
  });

  const removerFotoM = useMutation({
    mutationFn: () => atualizarIa({ iaFotoUrl: null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-info"] }),
  });

  function abrirEdicao() {
    setNome(ia?.nome === "IA" ? "" : (ia?.nome || ""));
    setModel(configs?.iaModel || "");
    setBaseUrl(configs?.iaBaseUrl || "");
    setSemTools(configs?.iaSemTools === "true");
    setTemperatura(configs?.iaTemperatura || "");
    setApiKey("");
    setMostrarChave(false);
    setErro("");
    setTesteResult(null);
    setEditando(true);
  }

  const chaveDefinida = !!(configs?.iaApiKey || configs?.iaApiKeyDefinida === "true");

  return (
    <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white">
          {ia?.fotoUrl ? (
            <img src={anexoUrlAbsoluto(ia.fotoUrl)} alt="IA" className="w-full h-full object-cover" />
          ) : (
            <Bot size={26} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {!editando && (
            <>
              <p className="text-sm font-semibold text-slate-800">{ia?.nome || "IA"}</p>
              <p className="text-xs text-slate-500">
                Assistente integrada ao chat. Clique para personalizar nome, foto e conexão com o GPT.
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <Cpu size={11} /> {configs?.iaModel || <em className="text-slate-400">modelo padrão</em>}
                </span>
                <span className="inline-flex items-center gap-1 truncate max-w-[280px]" title={configs?.iaBaseUrl || ""}>
                  <Globe size={11} /> {configs?.iaBaseUrl || <em className="text-slate-400">URL padrão</em>}
                </span>
                <span className="inline-flex items-center gap-1">
                  <KeyRound size={11} />{" "}
                  {chaveDefinida
                    ? <span className="text-emerald-600">chave configurada</span>
                    : <em className="text-amber-600">sem chave</em>}
                </span>
              </div>
            </>
          )}
        </div>
        {!editando && (
          <button
            onClick={abrirEdicao}
            className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-white/60"
            title="Editar IA"
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {editando && (
        <div className="mt-3 space-y-3">
          {/* Nome */}
          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">Nome da IA</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Nome da IA (ex.: Sofia, Aria, Jarvis…)"
              className="w-full px-3 py-1.5 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Modelo */}
          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1 inline-flex items-center gap-1">
              <Cpu size={11} /> Modelo
            </label>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="gpt-4o-mini, gpt-3.5-turbo, llama3…"
              className="w-full px-3 py-1.5 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 font-mono"
            />
          </div>

          {/* Base URL */}
          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1 inline-flex items-center gap-1">
              <Globe size={11} /> URL base (compatível com OpenAI)
            </label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.openai.com/v1"
              className="w-full px-3 py-1.5 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 font-mono"
            />
            <p className="text-[10px] text-slate-500 mt-0.5">
              Sem barra final. Ex.: <code>https://api.openai.com/v1</code> ou um servidor local.
            </p>
          </div>

          {/* API Key */}
          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1 inline-flex items-center gap-1">
              <KeyRound size={11} /> Chave da API
              {chaveDefinida && (
                <span className="text-[10px] text-emerald-600 font-normal">· já configurada (deixe vazio pra manter)</span>
              )}
            </label>
            <div className="relative">
              <input
                type={mostrarChave ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={chaveDefinida ? "•••••••• (vazio mantém a atual)" : "sk-..."}
                className="w-full pl-3 pr-9 py-1.5 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 font-mono"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setMostrarChave((v) => !v)}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                title={mostrarChave ? "Ocultar" : "Mostrar"}
              >
                {mostrarChave ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
            {chaveDefinida && (
              <button
                type="button"
                onClick={() => limparChaveM.mutate()}
                disabled={limparChaveM.isPending}
                className="text-[11px] text-red-600 hover:text-red-700 mt-1 disabled:opacity-50"
              >
                {limparChaveM.isPending ? "Removendo…" : "Remover chave salva"}
              </button>
            )}
          </div>

          {/* Temperatura */}
          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">
              Temperatura <span className="text-slate-400 font-normal">(0 = determinístico, 2 = criativo)</span>
            </label>
            <input
              type="number"
              min={0}
              max={2}
              step={0.1}
              value={temperatura}
              onChange={(e) => setTemperatura(e.target.value)}
              placeholder="0.2"
              className="w-32 px-3 py-1.5 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 font-mono"
            />
          </div>

          {/* Modo simples (sem function calling) */}
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={semTools}
              onChange={(e) => setSemTools(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Modo simples</span> — não enviar <code className="text-[10px]">tools</code>/<code className="text-[10px]">tool_choice</code>.
              <span className="block text-[10px] text-slate-500 mt-0.5">
                Marque quando o servidor não suportar function calling (LM Studio, Gemma, llama.cpp local etc.).
                A IA vira um chat simples — perde acesso a projetos/tarefas/clientes via tools.
              </span>
            </span>
          </label>

          {erro && <p className="text-xs text-red-600">{erro}</p>}

          {testeResult && (
            <p className={`text-xs inline-flex items-center gap-1 ${testeResult.ok ? "text-emerald-600" : "text-red-600"}`}>
              {testeResult.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
              {testeResult.msg}
            </p>
          )}

          <div className="flex gap-2 flex-wrap pt-1 border-t border-blue-200">
            <button
              onClick={() => salvarM.mutate()}
              disabled={salvarM.isPending}
              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
            >
              {salvarM.isPending ? "Salvando…" : "Salvar"}
            </button>
            <button
              onClick={() => testarM.mutate()}
              disabled={testarM.isPending}
              className="text-xs flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
              title="Testa a conexão com a URL e chave informadas (ou as já salvas, se vazias)"
            >
              <Plug size={11} /> {testarM.isPending ? "Testando…" : "Testar conexão"}
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploadM.isPending}
              className="text-xs flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded disabled:opacity-50"
            >
              <Upload size={11} /> {uploadM.isPending ? "Enviando…" : "Trocar foto"}
            </button>
            {ia?.fotoUrl && (
              <button
                onClick={() => removerFotoM.mutate()}
                disabled={removerFotoM.isPending}
                className="text-xs text-red-600 hover:text-red-700 px-2 py-1.5"
              >
                Remover foto
              </button>
            )}
            <button
              onClick={() => { setEditando(false); setErro(""); setApiKey(""); }}
              className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5 ml-auto"
            >
              Cancelar
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadM.mutate(f);
              if (fileRef.current) fileRef.current.value = "";
            }}
          />
        </div>
      )}
    </div>
  );
}
